import { getPathValue } from "./graphTraverser";

/**
 * Phase 4A.3/4A.4 — pure, unit-tested logic for the http-request and loop
 * node executors. No network / no supabase imports here (see nodePure.test.ts);
 * the engine wires these into workflowEngineService.executeNode.
 */

// ── http-request (4A.3) ──────────────────────────────────────────────────────

export interface HttpNodeConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string;
}

export const HTTP_ALLOWED_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
export const HTTP_TIMEOUT_MS = 30_000;
export const HTTP_MAX_RESPONSE_CHARS = 1_000_000; // ~1 MB of text

const PRIVATE_HOST_PATTERNS: RegExp[] = [
  /^localhost$/i,
  /^127\./,
  /^0\.0\.0\.0$/,
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^169\.254\./,          // link-local / cloud metadata
  /^\[?::1\]?$/,          // ipv6 loopback
  /^metadata\./i,
  /\.internal$/i,
  /\.local$/i,
];

/**
 * Validate an http-request node config before any fetch. HTTPS-only, method
 * whitelist, and private/internal hosts blocked (SSRF guard — the engine runs
 * in the browser today, but the same graph may run server-side later).
 */
export function validateHttpConfig(cfg: HttpNodeConfig | undefined): { ok: true; url: URL } | { ok: false; reason: string } {
  if (!cfg || !cfg.url || typeof cfg.url !== "string") return { ok: false, reason: "http-request node has no URL" };
  const method = (cfg.method || "GET").toUpperCase();
  if (!(HTTP_ALLOWED_METHODS as readonly string[]).includes(method)) {
    return { ok: false, reason: `method ${method} not allowed` };
  }
  let url: URL;
  try {
    url = new URL(cfg.url);
  } catch {
    return { ok: false, reason: `invalid URL: ${cfg.url}` };
  }
  if (url.protocol !== "https:") return { ok: false, reason: "only https:// URLs are allowed" };
  if (PRIVATE_HOST_PATTERNS.some((re) => re.test(url.hostname))) {
    return { ok: false, reason: `host ${url.hostname} is not allowed` };
  }
  return { ok: true, url };
}

/**
 * Replace {{path.to.value}} placeholders from the run state bag
 * ({ variables, nodeOutputs }). Unresolved paths become "" — never leak the
 * raw placeholder to an external service. Non-string resolved values are
 * JSON-stringified.
 */
export function interpolateTemplate(template: string, stateBag: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([\w.[\]-]+)\s*\}\}/g, (_m, path: string) => {
    const v = getPathValue(stateBag, path);
    if (v === undefined || v === null) return "";
    return typeof v === "string" ? v : JSON.stringify(v);
  });
}

// ── loop (4A.4) ──────────────────────────────────────────────────────────────

export const LOOP_MAX_ITERATIONS = 25; // hard safety cap regardless of loopCount

export interface LoopDecision {
  /** branch key the traverser matches against edge sourceHandle/label */
  branch: "loop" | "done";
  iteration: number;
}

/**
 * Bounded loop decision. Counter semantics: the loop node increments on every
 * arrival; while iteration <= loopCount it takes the "loop" branch (body),
 * afterwards "done". loopCount is clamped to LOOP_MAX_ITERATIONS.
 */
export function decideLoop(
  nodeId: string,
  loopCount: number | undefined,
  counters: Record<string, number>,
): LoopDecision {
  const cap = Math.min(Math.max(1, Math.floor(loopCount ?? 1)), LOOP_MAX_ITERATIONS);
  const iteration = (counters[nodeId] ?? 0) + 1;
  counters[nodeId] = iteration;
  if (iteration <= cap) return { branch: "loop", iteration };
  delete counters[nodeId]; // reset so a later re-entry starts fresh
  return { branch: "done", iteration };
}

/**
 * Per-node visit cap for the traversal loop (4A.4). Loop-free graphs keep the
 * legacy exactly-once behavior; graphs containing a loop node allow bounded
 * revisits so back-edges can cycle. Global maxSteps still backstops runaways.
 */
export function maxVisitsFor(hasLoopNode: boolean): number {
  return hasLoopNode ? LOOP_MAX_ITERATIONS + 2 : 1;
}
